import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { loadSshSettings } from "../terminal/TerminalContext"
import { useAuth } from "../auth/AuthContext"

const STORAGE_KEY = "deploy_session_id"
const AUTO_ACCEPT_KEY = "deploy_auto_accept"
const AI_PROPOSERS = ["claude", "codex"]

// Map SSE/poll state objects into a normalised deployState shape.
function applyServerState(prev, msg) {
  const t = msg.type ?? "state"
  const next = { ...prev }
  if (t === "state") {
    next.status = msg.status ?? prev.status
    next.id = msg.id ?? prev.id
    next.requested_by = msg.requested_by ?? prev.requested_by
    next.expected = Array.isArray(msg.expected) ? msg.expected : prev.expected
    next.responses = msg.responses ?? prev.responses
    next.project = msg.project ?? prev.project
    next.cancelled_by = msg.cancelled_by ?? prev.cancelled_by
    next.cancel_note = msg.cancel_note ?? prev.cancel_note
    next.timeout_s = msg.timeout_s ?? prev.timeout_s
    next.remaining_s = msg.remaining_s ?? prev.remaining_s
    next.title = msg.title ?? prev.title
    next.summary = msg.summary ?? prev.summary
    next.proposed_by = msg.proposed_by ?? prev.proposed_by
    if (Object.prototype.hasOwnProperty.call(msg, "proposal")) next.proposal = msg.proposal
  } else if (t === "deploy_request") {
    next.status = "awaiting_confirm"
    next.id = msg.deploy_id
    next.requested_by = msg.requested_by
    next.expected = msg.expected ?? []
    next.responses = msg.responses ?? {}
    next.project = msg.project ?? prev.project
    next.cancelled_by = null
    next.cancel_note = ""
    next.timeout_s = msg.timeout_s ?? prev.timeout_s
    next.remaining_s = msg.timeout_s ?? prev.timeout_s
    next.title = msg.title ?? ""
    next.summary = msg.summary ?? ""
    next.proposed_by = msg.proposed_by ?? ""
  } else if (t === "deploy_proposal") {
    next.proposal = msg.proposal ?? null
  } else if (t === "deploy_proposal_resolved") {
    next.proposal = null
  } else if (t === "deploy_response") {
    next.responses = msg.responses ?? prev.responses
    next.expected = Array.isArray(msg.expected) ? msg.expected : prev.expected
  } else if (t === "deploy_building") {
    next.status = "building"
    next.project = msg.project ?? prev.project
  } else if (t === "deploy_complete") {
    next.status = "complete"
    next.project = msg.project ?? prev.project
  } else if (t === "deploy_cancelled") {
    next.status = "cancelled"
    next.cancelled_by = msg.cancelled_by ?? null
    next.cancel_note = msg.note ?? ""
  } else if (t === "deploy_error") {
    next.status = "error"
    next.error = msg.error
    next.project = msg.project ?? prev.project
  }
  return next
}

function getSessionId() {
  let sid = sessionStorage.getItem(STORAGE_KEY)
  if (!sid) {
    sid = crypto.randomUUID()
    sessionStorage.setItem(STORAGE_KEY, sid)
  }
  return sid
}

const DeployEventContext = createContext(null)

export function DeployEventProvider({ children }) {
  const sessionId = getSessionId()
  const { user } = useAuth()
  const username = (user?.ssh_linked ? user.ssh_username : null) || loadSshSettings().username || user?.username || ""
  const [deployState, setDeployState] = useState({
    status: "idle", id: null, requested_by: null, expected: [], responses: {}, project: "ai-assistant",
    cancelled_by: null, cancel_note: "", timeout_s: 30, remaining_s: null,
    title: "", summary: "", proposed_by: "", proposal: null,
  })
  const deployStateRef = useRef(deployState)
  useEffect(() => { deployStateRef.current = deployState }, [deployState])

  // Auto-accept: when on, the SERVER confirms deploys for this user 10s in.
  // The stored preference lives server-side (per username, survives backend restarts);
  // localStorage is only a fallback cache for the first paint. Confirmation must not
  // depend on a browser tab being awake, because background/suspended tabs get their
  // timers throttled or dropped entirely.
  const [autoAccept, setAutoAcceptState] = useState(() => {
    try { return localStorage.getItem(AUTO_ACCEPT_KEY) === "1" } catch { return false }
  })
  const autoAcceptRef = useRef(autoAccept)
  const [aiAlwaysAllow, setAiAlwaysAllowState] = useState({ claude: false, codex: false })
  const setAutoAccept = useCallback((v) => {
    setAutoAcceptState(v)
    autoAcceptRef.current = v
    try { localStorage.setItem(AUTO_ACCEPT_KEY, v ? "1" : "0") } catch { /* ignore */ }
    fetch("/api/deploy/autoaccept/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, enabled: v }),
    }).catch(() => { /* SSE reconnect re-syncs the preference */ })
  }, [username])

  // Sync the toggle from the server — another browser/machine of the same user may
  // have changed it, and the server value is what auto-confirm actually acts on.
  useEffect(() => {
    if (!username) return
    let stale = false
    fetch(`/api/deploy/autoaccept/?username=${encodeURIComponent(username)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (stale || !data || typeof data.enabled !== "boolean") return
        setAutoAcceptState(data.enabled)
        autoAcceptRef.current = data.enabled
        try { localStorage.setItem(AUTO_ACCEPT_KEY, data.enabled ? "1" : "0") } catch { /* ignore */ }
      })
      .catch(() => { /* keep the cached value */ })
    return () => { stale = true }
  }, [username])

  useEffect(() => {
    if (!username) return
    let stale = false
    Promise.all(AI_PROPOSERS.map(async (agent) => {
      const response = await fetch(`/api/deploy/autoaccept/?username=${encodeURIComponent(username)}&agent=${agent}`)
      const data = response.ok ? await response.json() : null
      return [agent, !!data?.enabled]
    })).then((entries) => {
      if (!stale) setAiAlwaysAllowState(Object.fromEntries(entries))
    }).catch(() => {})
    return () => { stale = true }
  }, [username])

  const setAiAlwaysAllow = useCallback(async (agent, enabled) => {
    if (!AI_PROPOSERS.includes(agent)) return
    try {
      const response = await fetch("/api/deploy/autoaccept/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, agent, enabled }),
      })
      if (response.ok) setAiAlwaysAllowState((current) => ({ ...current, [agent]: enabled }))
    } catch { /* retain the last server-confirmed setting */ }
  }, [username])

  // True only when "complete" was set by a live event in this page session —
  // not from a stale server-state snapshot on initial load. Resets to false
  // on every page load (ref initialises as false).
  const sessionCompleteRef = useRef(false)
  // Same idea for "cancelled" — the cancel banner must only appear for a
  // cancellation witnessed live, never for a stale snapshot after a reload.
  const sessionCancelRef = useRef(false)

  const esRef = useRef(null)
  const healthPollRef = useRef(null)
  const statePollRef = useRef(null)

  // ── SSE connection ────────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (esRef.current) esRef.current.close()

    const params = new URLSearchParams({ session_id: sessionId })
    if (username) params.set("username", username)
    params.set("auto_accept", autoAcceptRef.current ? "1" : "0")
    const es = new EventSource(`/api/deploy/events/?${params}`)
    esRef.current = es

    // Track whether the first state snapshot from this SSE connection has been processed.
    // Each call to connect() gets its own closure variable so reconnects are handled correctly.
    let initialStateSeen = false

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        const t = msg.type

        if (t === "heartbeat") return

        const isInitial = t === "state" && !initialStateSeen
        if (t === "state") initialStateSeen = true

        if (t === "deploy_complete") {
          sessionCompleteRef.current = true
        }
        if (t === "deploy_cancelled") {
          sessionCancelRef.current = true
        }

        setDeployState((prev) => {
          const proposalSnapshot = Object.prototype.hasOwnProperty.call(msg, "proposal")
            ? { ...prev, proposal: msg.proposal }
            : prev
          if (isInitial) {
            // Suppress stale "complete" from server on a fresh page load —
            // the reload has already applied the update.
            if (msg.status === "complete" && !sessionCompleteRef.current) return proposalSnapshot
            // Same for a stale "cancelled" left over from before the reload.
            if (msg.status === "cancelled" && !sessionCancelRef.current) return proposalSnapshot
            // Don't let the server's "idle" (after a rebuild) clobber a
            // session-local "complete" that the health poll established.
            if (prev.status === "complete" && sessionCompleteRef.current) return proposalSnapshot
          }
          return applyServerState(prev, msg)
        })
      } catch { /* ignore parse errors */ }
    }

    es.onerror = () => {
      es.close()
      esRef.current = null
      startHealthPoll()
    }
  }, [sessionId, username])

  // ── Health poll after SSE dies (server restart path) ────────────────────────────
  // Server restarted → SSE died → poll until server is back → show "please reload" banner
  function startHealthPoll() {
    if (healthPollRef.current) return
    healthPollRef.current = setInterval(async () => {
      try {
        const r = await fetch("/api/health/")
        if (r.ok) {
          clearInterval(healthPollRef.current)
          healthPollRef.current = null
          sessionCompleteRef.current = true
          setDeployState((prev) => ({ ...prev, status: "complete" }))
          // Reconnect SSE so future deploys work without needing a page reload
          connect()
        }
      } catch { /* still down */ }
    }, 2000)
  }

  // ── State poll — SSE fallback so dialog/banner appear even if SSE event is missed ───
  useEffect(() => {
    statePollRef.current = setInterval(async () => {
      try {
        const r = await fetch("/api/deploy/state/")
        if (!r.ok) return
        const data = await r.json()
        setDeployState((prev) => {
          const proposalChanged = data.proposal?.id !== prev.proposal?.id
          const proposalSnapshot = proposalChanged ? { ...prev, proposal: data.proposal ?? null } : prev
          if (prev.status === "complete") {
            // Keep a session-local "complete" even if the server now reports idle
            // (it restarted and lost state). The banner must stay until the user reloads.
            if (data.status === "idle" && sessionCompleteRef.current) return proposalSnapshot
          }
          // Suppress "complete" from server only when we're starting fresh (idle) and
          // never saw the live event — avoids showing an old completed deploy on page load.
          // But if we were actively watching a build, trust the server reporting complete.
          if (data.status === "complete" && !sessionCompleteRef.current && prev.status !== "building") return proposalSnapshot
          if (data.status === "complete" && !sessionCompleteRef.current && prev.status === "building") {
            sessionCompleteRef.current = true
          }
          // Cancelled: trust it only when we watched the pending deploy live
          // (awaiting_confirm) — otherwise it's a stale pre-reload cancellation.
          if (data.status === "cancelled" && !sessionCancelRef.current && prev.status !== "awaiting_confirm") return proposalSnapshot
          if (data.status === "cancelled" && !sessionCancelRef.current && prev.status === "awaiting_confirm") {
            sessionCancelRef.current = true
          }
          const statusChanged = data.status !== prev.status || data.id !== prev.id || proposalChanged
          // Also sync responses during awaiting_confirm so chips update even if SSE missed
          const responsesChanged = prev.status === "awaiting_confirm" &&
            JSON.stringify(data.responses ?? {}) !== JSON.stringify(prev.responses ?? {})
          if (!statusChanged && !responsesChanged) return prev
          return applyServerState(prev, { type: "state", ...data })
        })
      } catch { /* network error */ }
    }, 2000)
    return () => clearInterval(statePollRef.current)
  }, [])

  useEffect(() => {
    connect()
    return () => {
      esRef.current?.close()
      if (healthPollRef.current) clearInterval(healthPollRef.current)
    }
  }, [connect])

  // ── Actions ───────────────────────────────────────────────────────────────────
  const confirm = useCallback(async (deployId, confirmed, note = "") => {
    await fetch("/api/deploy/confirm/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deploy_id: deployId, session_id: sessionId, confirmed, username, note }),
    })
  }, [sessionId, username])

  const requestDeploy = useCallback(async (sshCreds, project = "ai-assistant", metadata = {}) => {
    const res = await fetch("/api/deploy/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...sshCreds, session_id: sessionId, project, title: metadata.title, summary: metadata.summary }),
    })
    const data = await res.json()
    // Seed state directly from response so requester's dialog opens
    // immediately, independent of SSE delivery timing.
    if (data.ok && data.status === "awaiting_confirm") {
      setDeployState((prev) => applyServerState(prev, {
        type: "deploy_request",
        deploy_id: data.deploy_id,
        requested_by: data.requested_by ?? username,
        expected: data.expected ?? [username || sessionId],
        responses: {},
        project: data.project ?? project,
        timeout_s: data.timeout_s,
        title: data.title,
        summary: data.summary,
        proposed_by: data.proposed_by,
      }))
    }
    return data
  }, [sessionId, username])

  const respondProposal = useCallback(async (proposal, accepted) => {
    const ssh = loadSshSettings()
    if (accepted && !ssh.username) return { ok: false, error: "Bitte zuerst SSH in den System Settings konfigurieren." }
    const response = await fetch("/api/deploy/confirm/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        proposal_id: proposal.id,
        project: proposal.project,
        session_id: sessionId,
        username,
        confirmed: accepted,
        host: ssh.host,
        port: ssh.port,
        ssh_username: ssh.username,
        password: ssh.password,
      }),
    })
    const data = await response.json().catch(() => ({ error: `Deploy API: ${response.status}` }))
    if (data.ok && accepted && data.status === "awaiting_confirm") {
      setDeployState((prev) => applyServerState(prev, {
        type: "deploy_request", deploy_id: data.deploy_id, requested_by: data.requested_by ?? username,
        expected: data.expected ?? [username || sessionId], responses: { [username]: true },
        project: data.project, timeout_s: data.timeout_s, title: data.title,
        summary: data.summary, proposed_by: data.proposed_by,
      }))
    }
    return data
  }, [sessionId, username])

  const autoProposalRef = useRef("")
  useEffect(() => {
    const proposal = deployState.proposal
    if (!proposal || !aiAlwaysAllow[proposal.agent] || !loadSshSettings().username) return
    if (autoProposalRef.current === proposal.id) return
    autoProposalRef.current = proposal.id
    respondProposal(proposal, true)
  }, [aiAlwaysAllow, deployState.proposal, respondProposal])

  return (
    <DeployEventContext.Provider value={{ deployState, sessionId, confirm, requestDeploy, autoAccept, setAutoAccept, aiAlwaysAllow, setAiAlwaysAllow, respondProposal }}>
      {children}
    </DeployEventContext.Provider>
  )
}

export function useDeployEvents() {
  return useContext(DeployEventContext)
}
